import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Api } from '../api';
import { cleanLocalStorage, getDeviceToken } from '../utils/helper';
import ConfirmPopup from '../component/common/modals/ConfirmPopup';

/**
 * Signing out, from wherever the control happens to be.
 *
 * There are five places a person can log out — the sidebar, the topbar account
 * card, the old header, the widget topbar and the Elite Games topbar — and
 * until this hook each had its own copy. They had drifted apart in two ways
 * that both showed up as bugs:
 *
 *  - Only the sidebar asked for confirmation. Below `md` the sidebar collapses
 *    and a person signs out from the topbar instead, so on a tablet or phone
 *    one stray tap ended the session with no way back. The confirmation was
 *    not "missing on tablet" so much as never present outside the sidebar.
 *
 *  - Only two of the five revoked the device token. The other three cleared
 *    local storage and left the token live on the server, so push
 *    notifications kept going to a device nobody was signed in on.
 *
 * Usage — the caller renders the dialog wherever it likes:
 *
 *   const { requestLogout, logoutConfirm } = useLogout();
 *   <button onClick={requestLogout}>Log out</button>
 *   {logoutConfirm}
 */
export const useLogout = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestLogout = useCallback(() => setOpen(true), []);

  const logout = useCallback(async () => {
    setBusy(true);
    try {
      // A failed revoke must not strand someone on a page they meant to
      // leave, so the local sign-out happens either way.
      if (getDeviceToken()) await Api.logoutUser();
    } catch (error) {
      // Nothing to tell the user: they are being signed out regardless.
    } finally {
      cleanLocalStorage();
      setBusy(false);
      setOpen(false);
      navigate('/login');
    }
  }, [navigate]);

  const logoutConfirm = (
    <ConfirmPopup
      open={open}
      setOpen={setOpen}
      setAccepted={logout}
      isLoading={busy}
      message="Are you sure you want to logout?"
    />
  );

  return { requestLogout, logoutConfirm };
};

export default useLogout;
